import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../styles/HomePage.module.css';

const MusicGenreExplorer = () => {
  const navigate = useNavigate();
  const [currentGenreGroup, setCurrentGenreGroup] = useState(0);
  const [playingGenre, setPlayingGenre] = useState(null);
  const [isSwiping, setIsSwiping] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [hoveredGenre, setHoveredGenre] = useState(null);

  const audioRef = useRef(null);
  const containerRef = useRef(null);

  // Define genres with colors and icons similar to GenresPage
  const genres = [
    { 
      name: 'Country', 
      color: '#F59E0B', 
      icon: '🎸',
      description: 'Heartfelt lyrics with acoustic guitars, fiddles, and warm vocal storytelling.'
    },
    { 
      name: 'Classic Rock', 
      color: '#EF4444', 
      icon: '🤘',
      description: 'Powerful electric guitars, drums, and iconic vocals reminiscent of the 60s-80s.'
    },
    { 
      name: 'Pop', 
      color: '#EC4899', 
      icon: '💃',
      description: 'Catchy melodies, contemporary sounds, and upbeat rhythms.'
    },
    { 
      name: 'R&B', 
      color: '#8B5CF6', 
      icon: '🎤',
      description: 'Smooth vocals, soulful instrumentals, and emotional depth.'
    },
    { 
      name: 'Folk', 
      color: '#10B981', 
      icon: '🌿',
      description: 'Storytelling through acoustic instruments and authentic vocals.'
    },
    { 
      name: 'Electronic', 
      color: '#3B82F6', 
      icon: '⚡️',
      description: 'Modern beats, synthesizers, and innovative sound design.'
    },
    { 
      name: 'Jazz', 
      color: '#F97316', 
      icon: '🎷',
      description: 'Improvisation, syncopated rhythms, and rich harmonies with expressive solos.'
    },
    { 
      name: 'Hip-Hop', 
      color: '#6366F1', 
      icon: '🎧',
      description: 'Rhythmic vocals, beats, and samples with emphasis on wordplay and flow.'
    },
    { 
      name: 'Rap', 
      color: '#14B8A6', 
      icon: '🔥',
      description: 'Spoken word delivery over beats with focus on lyrical storytelling and rhythm.'
    }
  ];

  // Define genre groups as before
  const genreGroups = [
    [genres[0], genres[1], genres[2], genres[3]], // Country, Classic Rock, Pop, R&B
    [genres[4], genres[5], genres[6], genres[7]], // Folk, Electronic, Jazz, Hip-Hop
    [genres[8]], // Rap
  ];

  const genreSamplePaths = {
    Country: '/audio/country-sample.mp3',
    'Classic Rock': '/audio/classic-rock-sample.mp3',
    Pop: '/audio/pop-sample.mp3',
    'R&B': '/audio/rnb-sample.mp3',
    Folk: '/audio/folk-sample.mp3',
    Electronic: '/audio/electronic-sample.mp3',
    Jazz: '/audio/jazz-sample.mp3',
    'Hip-Hop': '/audio/hip-hop-sample.mp3',
    Rap: '/audio/rap-sample.mp3',
  };

  useEffect(() => {
    if (containerRef.current) {
      const translateValue = -currentGenreGroup * (100 / genreGroups.length);
      containerRef.current.style.transform = `translateX(${translateValue}%)`;
    }
  }, [currentGenreGroup, genreGroups.length]);

  // Mouse drag handling
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isMouseDown) return;

      e.preventDefault();
      setCurrentX(e.clientX);

      if (containerRef.current && startX !== 0) {
        const currentTranslate = -currentGenreGroup * (100 / genreGroups.length);
        const containerWidth = containerRef.current.offsetWidth;
        const diff = ((currentX - startX) / containerWidth) * 100;
        const cappedDiff = Math.min(Math.max(diff, -20), 20);
        containerRef.current.style.transform = `translateX(${currentTranslate + cappedDiff * 0.5}%)`;
      }
    };

    const handleMouseUp = () => {
      if (!isMouseDown) return;

      if (startX !== 0 && currentX !== 0) {
        const distance = startX - currentX;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe) {
          nextGenreGroup();
        } else if (isRightSwipe) {
          prevGenreGroup();
        } else {
          const translateValue = -currentGenreGroup * (100 / genreGroups.length);
          if (containerRef.current) {
            containerRef.current.style.transform = `translateX(${translateValue}%)`;
          }
        }
      }

      setIsMouseDown(false);
      setStartX(0);
      setCurrentX(0);
      setIsSwiping(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isMouseDown, startX, currentX, currentGenreGroup, genreGroups.length]);

  // Clean up audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.src = '';
        audioRef.current = null;
      }
    };
  }, []);

  const handleGenrePlay = (genre, e) => {
    e.preventDefault();
    e.stopPropagation();

    // Prevent action if swiping
    if (isSwiping) {
      return;
    }

    try {
      if (playingGenre === genre.name) {
        // Pause the current audio
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          audioRef.current.src = '';
          audioRef.current = null;
        }
        setPlayingGenre(null);
      } else {
        // Stop any currently playing audio
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          audioRef.current.src = '';
          audioRef.current = null;
        }

        // Create a new audio instance
        const newAudio = new Audio(genreSamplePaths[genre.name]);
        audioRef.current = newAudio;

        // Play the new audio
        const playPromise = newAudio.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setPlayingGenre(genre.name);
            })
            .catch((error) => {
              console.error(`Failed to play audio for ${genre.name}:`, error);
              setPlayingGenre(null);
              audioRef.current = null;
            });
        }

        // Set up the onEnded handler
        newAudio.onended = () => {
          setPlayingGenre(null);
          audioRef.current = null;
        };
      }
    } catch (error) {
      console.error(`Error in handleGenrePlay for ${genre.name}:`, error);
      setPlayingGenre(null);
      if (audioRef.current) {
        audioRef.current = null;
      }
    }
  };

  const handleKeyDown = (genre, e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleGenrePlay(genre, e);
    }
  };

  const prevGenreGroup = () => {
    setCurrentGenreGroup((prev) => (prev === 0 ? genreGroups.length - 1 : prev - 1));
    setPlayingGenre(null);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = '';
      audioRef.current = null;
    }
  };

  const nextGenreGroup = () => {
    setCurrentGenreGroup((prev) => (prev === genreGroups.length - 1 ? 0 : prev + 1));
    setPlayingGenre(null);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = '';
      audioRef.current = null;
    }
  };

  const handleGetStarted = () => {
    navigate('/packages');
    setTimeout(() => window.scrollTo(0, 0), 100);
  };

  // Touch handlers for swipe
  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
    setTouchEnd(null);
    setIsSwiping(false); // Reset swiping state
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.touches[0].clientX);

    if (touchStart && containerRef.current) {
      const diff = touchStart - e.touches[0].clientX;
      if (Math.abs(diff) > 20) { // Increased threshold to 20 pixels
        setIsSwiping(true);
      }

      const currentTranslate = -currentGenreGroup * (100 / genreGroups.length);
      const containerWidth = containerRef.current.offsetWidth;
      const cappedDiff = Math.min(Math.max(((e.touches[0].clientX - touchStart) / containerWidth) * 100, -20), 20);
      containerRef.current.style.transform = `translateX(${currentTranslate + cappedDiff * 0.5}%)`;
    }
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setIsSwiping(false);
      return;
    }

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextGenreGroup();
    } else if (isRightSwipe) {
      prevGenreGroup();
    } else {
      const translateValue = -currentGenreGroup * (100 / genreGroups.length);
      if (containerRef.current) {
        containerRef.current.style.transform = `translateX(${translateValue}%)`;
      }
    }

    setTouchStart(null);
    setTouchEnd(null);
    setIsSwiping(false);
  };

  const handleMouseDown = (e) => {
    setIsMouseDown(true);
    setStartX(e.clientX);
    setCurrentX(e.clientX);
    setIsSwiping(true);

    if (containerRef.current) {
      containerRef.current.style.cursor = 'grabbing';
    }

    e.preventDefault();
  };

  return (
    <div className={styles.genreExplorerSection}>
      <h2>Explore Our Music Styles</h2>
      <p className={styles.sectionSubheadline}>Discover the Perfect Sound for Your Story</p>

      <p className={styles.mobileSwipeInstruction}>← Swipe to explore more styles →</p>

      <div className={styles.genreCarouselContainer}>
        <div className={styles.arrowHintContainer}>
          <div className={styles.leftArrowHint}>
            <span className={styles.arrowHintText}>← More styles</span>
          </div>
          <div className={styles.rightArrowHint}>
            <span className={styles.arrowHintText}>More styles →</span>
          </div>
        </div>

        <button
          className={`${styles.carouselButton} ${styles.desktopLeftButton}`}
          onClick={prevGenreGroup}
          aria-label="Previous genres"
        >
          ‹
        </button>

        <div className={styles.genreCirclesWrapper}>
          <div
            ref={containerRef}
            className={styles.genreCirclesContainer}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
          >
            {genreGroups.map((group, groupIndex) => (
              <div key={groupIndex} className={styles.genreGroupSlide}>
                <div className={styles.genreCardsGrid}>
                  {group.map((genre) => (
                    <div
                      key={genre.name}
                      className={`${styles.genreCard} ${playingGenre === genre.name ? styles.playing : ''} ${
                        hoveredGenre === genre.name ? styles.hovered : ''
                      }`}
                      onClick={(e) => handleGenrePlay(genre, e)}
                      onKeyDown={(e) => handleKeyDown(genre, e)}
                      onMouseEnter={() => setHoveredGenre(genre.name)}
                      onMouseLeave={() => setHoveredGenre(null)}
                      role="button"
                      tabIndex={0}
                      aria-label={playingGenre === genre.name ? `Pause ${genre.name}` : `Play ${genre.name}`}
                      style={{ '--genre-color': genre.color }}
                    >
                      <div className={styles.genreCardHeader}>
                        <span className={styles.genreIcon}>{genre.icon}</span>
                        <h3>{genre.name}</h3>
                      </div>
                      <p className={styles.genreDescription}>{genre.description}</p>
                      <button 
                        className={`${styles.sampleButton} ${playingGenre === genre.name ? styles.playing : ''}`}
                        onClick={(e) => handleGenrePlay(genre, e)}
                        aria-label={playingGenre === genre.name ? `Pause ${genre.name}` : `Play ${genre.name}`}
                      >
                        {playingGenre === genre.name ? (
                          <svg className={styles.pauseIcon} viewBox="0 0 24 24" fill="currentColor">
                            <rect x="6" y="4" width="4" height="16" />
                            <rect x="14" y="4" width="4" height="16" />
                          </svg>
                        ) : (
                          <svg className={styles.playIcon} viewBox="0 0 24 24" fill="currentColor">
                            <polygon points="5 3 19 12 5 21 5 3" />
                          </svg>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          className={`${styles.carouselButton} ${styles.desktopRightButton}`}
          onClick={nextGenreGroup}
          aria-label="Next genres"
        >
          ›
        </button>
      </div>

      <div className={styles.carouselDots}>
        {genreGroups.map((_, index) => (
          <span
            key={index}
            className={`${styles.dot} ${currentGenreGroup === index ? styles.activeDot : ''}`}
            onClick={() => setCurrentGenreGroup(index)}
          ></span>
        ))}
      </div>

      <button className={styles.startMySongButton} onClick={handleGetStarted}>
        Create Your Song Now
      </button>

      <div className={styles.floatingNote1}></div>
      <div className={styles.floatingNote2}></div>
      <div className={styles.floatingNote3}></div>
      <div className={styles.floatingNote4}></div>
    </div>
  );
};

export default MusicGenreExplorer;